import styled, { css } from 'styled-components';

import { Link, zIndex } from '@comicrelief/component-library';
import arrowIcon from '../assets/arrow-icon.svg';

const transitionDuration = 0.2;

const redArrowAfter = css`
  &::after {
    content: '';
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    mask-image: url(${arrowIcon});
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url(${arrowIcon});
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    background-color: ${({ theme }) => theme.color('red')};
    opacity: 0;
    transition: opacity 0.15s ease-out;
  }

  &:hover::after,
  &:focus::after {
    opacity: 1;
  }
`;

const hoverLinkStyles = css`
  color: ${({ theme }) => theme.color('red')};
  background-color: ${({ theme }) => theme.color('grey_extra_light')};
  font-weight: 600;
`;

const NavLinkClass = styled(Link)`
  display: inline-block;
  border: 0;
  padding: 20px 25px;
  line-height: 1rem;
  height: auto;
  font-weight: 600;
  width: 100%;
  color: ${({ theme }) => theme.color('black')};
  transition: color ${transitionDuration}s ease;
  text-decoration: none;
  outline-offset: -3px;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    outline-offset: 0;
  }

  :hover,
  :focus,
  :focus-within {
    border: 0;
  }

  :focus {
    ${hoverLinkStyles}
  }

  // Only add HOVER styles to non-touch devices to avoid weird touch issues (see ENG-4709):
  @media (hover: hover) {
    :hover {
      ${hoverLinkStyles}
    }
  }

  // Reaffirm them for ALL device types once we hit the non-mobile nav:
  @media ${({ theme }) => theme.breakpoints2026('L')} {
    :hover {
      ${hoverLinkStyles}
    }
  }

  :focus-within {
    // Reset the colour of a parent...
    background-color: ${({ theme }) => theme.color('white')};

    // ...unless it's also being hovered
    &:hover {
      background-color: ${({ theme }) => theme.color('grey_extra_light')};
    }
  }
`;

const SecondaryNavMenuWrapper = styled.ul`
  display: flex;
  padding: 0;
  list-style: none outside;
  left: 0;
  top: 0;

  position: absolute;
  width: 100%;
  ${zIndex('higher')};

  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.color('white')};
  border: 1px solid ${({ theme }) => theme.color('grey_medium')};
  border-radius: 16px;
  overflow: hidden;

  /* Mobile slide-in animation */
  transform: ${({ isSubMenuOpen }) => (isSubMenuOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.15s ease-out;
  visibility: ${({ isSubMenuOpen }) => (isSubMenuOpen ? 'visible' : 'hidden')};

  /* Remove border from first item only on mobile */
  > div:first-of-type > li:first-of-type {
    border-top: none;
  }

  /* When tertiary menu is open, hide the entire secondary menu on mobile */
  ${({ isTertiaryOpen }) => isTertiaryOpen && css`
    display: none;
  `}

  // See 'iOS scrollbar overlap fix' in Navs.style.js 
  width: calc(100% - 20px);
  left: 10px;

  // DESKTOP:
  @media ${({ theme }) => theme.breakpoints2026('L')} {
    background-color: ${({ theme }) => theme.color('white')};
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: calc(100% + 2px);
    height: auto;
    padding: 0;
    position: absolute;
    top: 100%;
    left: -1px;
    border-radius: 0 0 25px 25px;
    border: 1px solid ${({ theme }) => theme.color('grey_medium')};
    overflow: visible;
    transition: opacity 0.4s linear, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), visibility 0s linear 0.4s;
    transform: scaleY(0);
    transform-origin: top;
    opacity: 0;
    visibility: hidden;
    box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.05);
    align-items: stretch;

    // Invisible bridge above dropdown to maintain hover state
    &::before {
      content: '';
      position: absolute;
      top: -20px;
      left: 0;
      width: 100%;
      height: 20px;
      background: transparent;
    }
  }
`;

const ColumnWrapper = styled.span`
  display: contents;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    border-right: 1px solid ${({ theme }) => theme.color('grey_medium')};
    opacity: 0;
    transform: translateY(-10px);
    transition: opacity 0.3s ease-out 0.15s, transform 0.3s ease-out 0.15s;
    overflow: hidden;
    padding: 11px 0 8px 0;

    &:last-of-type {
      border-right: none;
    }
  }
`;

const SecondaryNavItem = styled.li`
  padding: 0;
  height: 100%;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color('grey_medium')};
  position: relative;
  transition: background-color ${transitionDuration}s ease;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    height: auto;
    border-top: none;

    span {
      font-weight: ${({ $isSecondary }) => ($isSecondary ? 700 : 500)};
    }

    ${({ $isSecondary }) => $isSecondary && css`
      border-top: 1px solid ${({ theme }) => theme.color('grey_medium')};
      margin-top: 8px;
      padding-top: 8px;

      &:first-of-type {
        border-top: none;
        margin-top: 0;
        padding-top: 0;
      }
    `}
  }
`;

const SecondaryNavLink = styled(NavLinkClass)`
  padding: 20px 25px;
  color: ${({ theme }) => theme.color('black')};
  height: auto;
  position: relative;

  span {
    font-weight: 600;
  }

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    padding: 12px 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 1rem;
    transform: translateX(0);
    transition: transform 0.35s cubic-bezier(0.34, 2.12, 0.64, 1);

    span {
      font-size: 1rem;
      font-weight: ${({ $isSecondary }) => ($isSecondary ? 700 : 500)};
    }

    &:hover:not(:focus-visible) {
      background-color: transparent;
      transform: translateX(5px);
      transition: transform 0.35s cubic-bezier(0.34, 2.12, 0.64, 1);
    }

    &:focus-visible {
      background-color: transparent;
      transform: translateX(0);
    }

    ${redArrowAfter}
  }

`;

const SecondaryNavLinkWithChildren = styled(SecondaryNavLink)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecondaryChevronWrapper = styled.span`
  width: 8px;
  flex-shrink: 0;

  img {
    width: 100%;
    display: block;
    height: auto;
  }
`;

const TertiaryNavMenu = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
  list-style: none outside;
  width: 100%;
  ${zIndex('higher')};

  background-color: ${({ theme }) => theme.color('white')};

  position: absolute;
  left: 0;
  top: 0;
  border: 1px solid ${({ theme }) => theme.color('grey_medium')};
  border-radius: 16px;
  overflow: hidden;

  /* Mobile slide-in animation */
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.15s ease-out;
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};

  // See 'iOS scrollbar overlap fix' in Navs.style.js 
  width: calc(100% - 20px);
  left: 10px;

  // hide on desktop
  @media ${({ theme }) => theme.breakpoints2026('L')} {
    display: none;
  }
`;

const TertiaryNavItem = styled.li`
  padding: 0;
  height: auto;
  width: 100%;
  flex: 0 0 auto;
  border-top: 1px solid ${({ theme }) => theme.color('grey_medium')};
  position: relative;
  transition: background-color ${transitionDuration}s ease;

  &:first-of-type {
    border-top: none;
  }


  @media ${({ theme }) => theme.breakpoints2026('L')} {
    span {
      font-weight: 100;
    }
  }
`;

const TertiaryNavLink = styled(NavLinkClass)`
  padding: 20px 25px;
  color: ${({ theme }) => theme.color('black')};
  height: auto;
  position: relative;

  span {
    font-weight: ${({ $isParent }) => ($isParent ? 600 : 500)};
  }
`;

const NavCard = styled.a`
  display: none;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    text-decoration: none;
    color: ${({ theme }) => theme.color('black')};
    padding: 13px 30px 13px 20px;
    outline-offset: -3px;
    transform: translateX(0);

    &,
    img {
      transition: transform 0.35s cubic-bezier(0.34, 2.12, 0.64, 1);
    }

    // Targets the NavCardImage in an oldskool way so we're able to hook into the hover state, using the 
    img {
      // Zoomed-in slightly so the bounce back-and-forth doesn't end up actually resizing the image
      transform: scale(1.02);
    }

    ${redArrowAfter}

    &::after {
      align-self: flex-start;
      margin-top: 4px;
    }

    &:hover:not(:focus-visible) {
      transform: translateX(5px);

      img {
        transform: scale(1.07);
      }
    }

    &:focus-visible {
      transform: translateX(0);

      img {
        transform: scale(1.02);
      }
    }
  }
`;

const NavCardImage = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const NavCardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const NavCardTitle = styled.span`
  display: block;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.color('black')};
  transition: color 0.15s ease-out;

  ${NavCard}:hover &,
  ${NavCard}:focus & {
    color: ${({ theme }) => theme.color('red')};
  }
`;

const NavCardDescription = styled.span`
  display: block;
  font-family: Montserrat, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: ${({ theme }) => theme.color('grey_dark')};
  line-height: 1.4;
`;

const SecondaryMenuPrimaryLink = styled.li`
  padding: 0;
  width: 100%;
  display: block;

  @media ${({ theme }) => theme.breakpoints2026('L')} {
    display: none;
  }
`;

const SecondaryMenuPrimaryLinkAnchor = styled(NavLinkClass)`
  padding: 20px 25px;
  color: ${({ theme }) => theme.color('black')};
  height: auto;
  position: relative;

  span {
    font-weight: 600;
  }
`;

export {
  SecondaryNavMenuWrapper,
  ColumnWrapper,
  SecondaryNavItem,
  SecondaryNavLink,
  SecondaryNavLinkWithChildren,
  SecondaryChevronWrapper,
  TertiaryNavMenu,
  TertiaryNavItem,
  TertiaryNavLink,
  NavCard,
  NavCardImage,
  NavCardContent,
  NavCardTitle,
  NavCardDescription,
  SecondaryMenuPrimaryLink,
  SecondaryMenuPrimaryLinkAnchor
};
